import { useEffect, useCallback } from 'react';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from '../context/FormContext';
import { useUtmTracking } from '../hooks/useUtmTracking';
import type { Question } from '../types';
import { submitApplication } from '../lib/webhook';
import { computePerfil } from '../lib/segmentation';
import { trackEvent, trackCustomEvent, sendHitFunnel } from '../lib/tracking';
import { UrgencyBanner } from '../components/shared/UrgencyBanner';
import { Logo } from '../components/shared/Logo';
import { ProgressBar } from '../components/form/ProgressBar';
import { QuestionCard } from '../components/form/QuestionCard';
import { TextQuestion } from '../components/form/TextQuestion';
import { SingleChoiceQuestion } from '../components/form/SingleChoiceQuestion';
import { WhatsAppInput } from '../components/form/WhatsAppInput';
import { NavigationButtons } from '../components/form/NavigationButtons';

function validateAnswer(q: Question, value: string): boolean {
  if (!value || value.trim() === '') return false;

  switch (q.type) {
    case 'text-short':
      return value.trim().length >= 2;
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
    case 'text-long':
      return value.length >= (q.minLength ?? 0) && value.length <= (q.maxLength ?? Infinity);
    case 'single-choice':
      return q.options?.includes(value) ?? false;
    case 'phone-intl':
      return value.length >= 7;
    default:
      return false;
  }
}

export default function ApplicationPage() {
  const navigate = useNavigate();
  const { answers, questions, currentStep, totalSteps, setAnswer, goNext, goBack } = useFormContext();
  const { utmParams } = useUtmTracking();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [phoneValid, setPhoneValid] = useState(false);

  const question = questions[currentStep] ?? questions[questions.length - 1];
  const currentAnswer = answers[question.id] ?? '';
  const isLastStep = currentStep === totalSteps - 1;

  const isCurrentValid = question.type === 'phone-intl'
    ? phoneValid
    : validateAnswer(question, currentAnswer);

  useEffect(() => {
    trackEvent('PageView');
    trackCustomEvent('ApplicationStart', { step: 0 });
    sendHitFunnel('visita'); // embudo orgánico → mc-events (hit_mentor)
  }, []);

  useEffect(() => {
    if (currentStep > 0) {
      trackCustomEvent(`ApplicationStep_${currentStep}`, { step: currentStep });
    }
  }, [currentStep]);

  const handleNext = useCallback(async () => {
    if (!isCurrentValid || submitting) return;

    if (!isLastStep) {
      goNext();
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const result = await submitApplication({
      answers,
      utmParams,
      countryDetected: localStorage.getItem('hit_country_detected') ?? 'NI',
    });

    if (result.success) {
      // Lead NO va acá: el Lead ÚNICO del embudo es el de la captura (hit-vsl,
      // navegador + CAPI deduplicado). Disparaba también aquí y contaba DOS Leads
      // por persona. El paso "aplicación completada" se mide con CompleteRegistration
      // en la página de agenda (SchedulingPage), no con un segundo Lead.
      sendHitFunnel('aplicacion'); // aplicación completada → mc-events (hit_mentor)

      localStorage.removeItem('hit_application_answers');
      localStorage.removeItem('hit_application_current_step');

      // Tier de calidad (A/B/C/D) para enrutar el calendario en /agenda:
      // A/B → calendario de prioridad, C/D → calendario normal. Es el MISMO
      // cálculo puro que va en el payload del webhook (no un scorer nuevo).
      const { tier } = computePerfil(answers);

      const params = new URLSearchParams({
        ...(Object.fromEntries(
          Object.entries(utmParams).filter(([, v]) => Boolean(v))
        ) as Record<string, string>),
        name: answers.nombre ?? '',
        email: answers.email ?? '',
        phone: answers.whatsapp ?? '',
        tier,
      });

      navigate(`/agenda?${params.toString()}`);
    } else {
      setSubmitError(
        '⚠️ Hubo un problema al enviar tu aplicación. Tu información se guardó localmente. Intenta nuevamente o contacta a soporte.'
      );
      setSubmitting(false);
    }
  }, [isCurrentValid, submitting, isLastStep, goNext, answers, utmParams, navigate]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // Solo procesa Enter cuando el foco está en un campo de texto;
      // si el foco está en un botón (ATRÁS), el click handler ya se encarga.
      const targetTag = (e.target as HTMLElement).tagName;
      const isTextField = targetTag === 'INPUT' || targetTag === 'TEXTAREA';

      if (
        e.key === 'Enter' &&
        isTextField &&
        isCurrentValid &&
        question.type !== 'text-long' &&
        question.type !== 'single-choice'
      ) {
        handleNext();
      }
      if (e.key === 'Escape' && currentStep > 0 && !isLastStep) {
        goBack();
      }
    },
    [isCurrentValid, question.type, currentStep, isLastStep, handleNext, goBack],
  );

  const handlePhoneChange = (value: string, valid: boolean) => {
    setAnswer(question.id, value);
    setPhoneValid(valid);
  };

  const renderInput = () => {
    if (question.type === 'single-choice') {
      return (
        <SingleChoiceQuestion
          id={question.id}
          options={question.options ?? []}
          value={currentAnswer}
          onChange={val => setAnswer(question.id, val)}
          onAutoAdvance={handleNext}
        />
      );
    }

    if (question.type === 'phone-intl') {
      return (
        <WhatsAppInput
          value={currentAnswer}
          onChange={handlePhoneChange}
        />
      );
    }

    return (
      <TextQuestion
        id={question.id}
        type={question.type}
        value={currentAnswer}
        placeholder={question.placeholder}
        minLength={question.minLength}
        maxLength={question.maxLength}
        onChange={val => setAnswer(question.id, val)}
        onSubmit={handleNext}
        isValid={isCurrentValid}
      />
    );
  };

  return (
    <div
      className="min-h-screen bg-hit-black flex flex-col"
      onKeyDown={handleKeyDown}
    >
      <header className="sticky top-0 z-40 bg-hit-black">
        <UrgencyBanner
          icon="🔥"
          message="Plazas limitadas para HIT 2026 — Aplica ahora"
          variant="urgent"
        />
        <div className="pt-4 pb-2">
          <Logo />
          <p className="text-center text-xs text-hit-gray-mid px-4 mt-1">
            Aplicación a la Certificación HIT
          </p>
        </div>
        <ProgressBar current={currentStep} total={totalSteps} />
      </header>

      <main className="flex-1 flex flex-col justify-between pt-4 max-w-xl mx-auto w-full">
        <QuestionCard
          stepKey={currentStep}
          label={question.question}
          helper={question.helper}
        >
          {renderInput()}
        </QuestionCard>

        {submitError && (
          <div className="mx-4 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {submitError}
            <button
              type="button"
              onClick={handleNext}
              className="block mt-2 font-bold underline"
            >
              Reintentar
            </button>
          </div>
        )}

        {question.type !== 'single-choice' && (
          <NavigationButtons
            onNext={handleNext}
            onBack={goBack}
            canGoBack={currentStep > 0}
            canGoNext={isCurrentValid}
            isSubmitting={submitting}
            isLastStep={isLastStep}
          />
        )}

        {question.type === 'single-choice' && currentStep > 0 && (
          <div className="px-4 pb-8">
            <button
              type="button"
              onClick={goBack}
              className="text-hit-gray-mid text-sm font-medium py-2 hover:text-hit-white transition-colors w-full text-center"
            >
              ← ATRÁS
            </button>
          </div>
        )}
      </main>

      <footer className="text-center py-4 px-4">
        <p className="text-xs text-hit-gray-dark">
          📊 Recibimos +100 aplicaciones diarias. No todos califican.
        </p>
      </footer>
    </div>
  );
}
